import type { ApiResult, CheckoutSession } from './api';

/**
 * Status polling of GET /checkout/sessions/:token. Only this, never a signal
 * from the popup, decides that a payment succeeded or failed: provider pages
 * cannot message us, and `popup.closed` reads true under a provider's COOP even
 * while the window is still open.
 */

export const FAST_INTERVAL_MS = 2_000;
export const SLOW_INTERVAL_MS = 5_000;
export const FAST_PHASE_MS = 60_000;
export const NETWORK_RETRY_MS = 5_000;
export const RATE_LIMIT_DEFAULT_MS = 10_000;
export const RATE_LIMIT_MIN_MS = 5_000;
export const POLL_MAX_MS = 30 * 60_000;

export type PollOutcome =
  | { kind: 'ok' }
  | { kind: 'http_error' }
  | { kind: 'rate_limited'; retryAfterSec: number | null }
  | { kind: 'network_error' };

/** The delay before the next poll, or null when polling should give up. */
export function nextDelay(elapsedMs: number, last: PollOutcome): number | null {
  if (elapsedMs >= POLL_MAX_MS) return null;
  switch (last.kind) {
    case 'rate_limited':
      return Math.max((last.retryAfterSec ?? 0) * 1000 || RATE_LIMIT_DEFAULT_MS, RATE_LIMIT_MIN_MS);
    case 'network_error':
      return NETWORK_RETRY_MS;
    default:
      return elapsedMs < FAST_PHASE_MS ? FAST_INTERVAL_MS : SLOW_INTERVAL_MS;
  }
}

export function outcomeOf(res: ApiResult<unknown>): PollOutcome {
  if (res.ok) return { kind: 'ok' };
  if (res.status === 0) return { kind: 'network_error' };
  if (res.status === 429) return { kind: 'rate_limited', retryAfterSec: res.retryAfterSec };
  return { kind: 'http_error' };
}

export interface PollerOptions {
  fetchSession(): Promise<ApiResult<CheckoutSession>>;
  /** Called with every fresh status; return true to stop polling. */
  onSession(session: CheckoutSession): boolean;
  onGiveUp(): void;
  now?: () => number;
  doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>;
}

export function startPoller(opts: PollerOptions): () => void {
  const now = opts.now ?? (() => Date.now());
  const doc = opts.doc ?? document;
  const startedAt = now();
  let timer: ReturnType<typeof setTimeout> | null = null;
  let stopped = false;
  let inFlight = false;

  const stop = () => {
    stopped = true;
    if (timer) clearTimeout(timer);
    timer = null;
    doc.removeEventListener('visibilitychange', onVisible);
  };

  async function tick(): Promise<void> {
    if (stopped || inFlight) return;
    if (timer) clearTimeout(timer);
    timer = null;
    inFlight = true;
    const res = await opts.fetchSession();
    inFlight = false;
    if (stopped) return;
    if (res.ok && opts.onSession(res.data)) {
      stop();
      return;
    }
    const delay = nextDelay(now() - startedAt, outcomeOf(res));
    if (delay === null) {
      stop();
      opts.onGiveUp();
      return;
    }
    timer = setTimeout(() => void tick(), delay);
  }

  function onVisible(): void {
    // Customers come back to the tab right after paying in the popup; answer
    // them now rather than at the next slow tick.
    if (doc.visibilityState === 'visible') void tick();
  }

  doc.addEventListener('visibilitychange', onVisible);
  void tick();
  return stop;
}
